import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useCallback, useState } from "react";

// EDS shadcn/ui components - already in collins-mobile
import { EDSButton } from "../../../../eds/EDSButton";
import { EDSDatePicker } from "../../../../eds/EDSDatePicker";

// Hooks from collins-mobile
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { getOpenCloseTimeInMinutes } from "../../../../../utils/booking";

import { useUrlNavigation } from "@/hooks/useUrlSearchParams";

export const DateSelector = () => {
  const {
    date,
    venueConfig,
    gridConfig,
    setDiaryContext,
    selectedVenue,
    selectedView
  } = useDiaryContext();
  const { connectionStatus } = useDeviceContext();
  const { updateUrl } = useUrlNavigation();
  const [internalDate, setInternalDate] = useState(date);
  const dateToday = dayjs().toDate();

  React.useEffect(() => {
    setInternalDate(date);
  }, [date]);

  const updateDate = useCallback(
    (newDate: Date) => {
      if (
        newDate.toISOString().split("T")[0] === date.toISOString().split("T")[0]
      ) {
        // TODO: should probably refresh current booking list
        return;
      }

      setInternalDate(newDate);
      const gridConfigUpdated = {
        ...gridConfig,
        ...getOpenCloseTimeInMinutes({
          date: newDate,
          openingTimes: venueConfig?.opening_times
        })
      };

      setDiaryContext({
        date: newDate,
        isInitialising: true,
        bookings: undefined,
        bookingsFiltered: undefined,
        gridConfig: gridConfigUpdated,
        selectedBooking: undefined,
        selectedBookingV4: undefined
      });

      if (selectedVenue && selectedView) {
        updateUrl({
          venueId: selectedVenue,
          tab: selectedView,
          date: newDate
        });
      }

      // Blur any focused elements to prevent focus flashing on re-renders
      (document.activeElement as HTMLElement)?.blur();
    },
    [
      gridConfig,
      venueConfig?.opening_times,
      setDiaryContext,
      date,
      selectedVenue,
      selectedView,
      updateUrl
    ]
  );

  // Format date for display

  // Check if current date is today
  const isToday = () => {
    const dateTime = dayjs(internalDate);
    const today = dayjs();
    return dateTime.isSame(today, "day");
  };

  // Handle date selection from calendar
  const handleDateSelect = useCallback(
    (selectedDate: Date | undefined) => {
      if (selectedDate) {
        // Fix timezone issue by creating a new date object with proper timezone handling
        // Use the selected date's year, month, and day but set time to noon to avoid timezone issues
        const fixedDate = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          12, // Set to noon to avoid timezone boundary issues
          0,
          0
        );
        updateDate(fixedDate);
      }
    },
    [updateDate]
  );

  return (
    <div className="DateSelector--unified flex items-center gap-3 justify-center">
      {/* Today Button - Separate for quick access */}
      <EDSButton
        disabled={connectionStatus !== "internet"}
        variant={isToday() ? "default" : "outline"}
        size="md"
        onClick={() => updateDate(dateToday)}
        className="DateSelector__todayButton font-semibold transition-all duration-200"
      >
        Today
      </EDSButton>

      {/* Unified Date Navigation Container */}
      <div className="DateSelector__unifiedContainer inline-flex items-center bg-card border-2 rounded-[var(--radius)] transition-all duration-300 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] gap-3 hover:border-ring">
        {/* Previous Day Button */}
        <EDSButton
          aria-label="Previous Day"
          disabled={connectionStatus !== "internet"}
          variant="ghost"
          size="icon"
          onClick={() =>
            updateDate(dayjs(internalDate).subtract(1, "day").toDate())
          }
          className="DateSelector__navButton"
        >
          <ChevronLeft className="DateSelector__icon size-4" />
        </EDSButton>

        {/* EDS Date Picker */}
        <EDSDatePicker
          date={internalDate}
          onSelect={handleDateSelect}
          placeholder="Select date"
          disabled={connectionStatus !== "internet"}
          className="DateSelector__datePicker relative"
          weekdayFormat="long"
        />

        {/* Next Day Button */}
        <EDSButton
          aria-label="Next Day"
          disabled={connectionStatus !== "internet"}
          variant="ghost"
          size="icon"
          onClick={() => updateDate(dayjs(internalDate).add(1, "day").toDate())}
          className="DateSelector__navButton"
        >
          <ChevronRight className="DateSelector__icon size-4" />
        </EDSButton>
      </div>
    </div>
  );
};
